import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchCustomerDetail } from '#/api/crmMgmt/customer/fetchDetail';
import {
  fetchCustomerSelectOptions,
} from '#/api/crmMgmt/customer/fetchSelectOptions';
import { createSalesOrder } from '#/api/crmMgmt/salesOrder/create';
import { ROUTES } from '#/config/routes';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import FloatingSelect from '#/modules/commonComponents/form/select';
import CustomerOrderPickerDialog from '#/modules/crmMgmt/customerOrder/components/CustomerOrderPickerDialog';
import CreateSalesOrderScheduleForm from '#/modules/crmMgmt/salesOrder/components/CreateSalesOrderScheduleForm';
import LinkedCustomerOrderInfoCard from '#/modules/crmMgmt/salesOrder/components/LinkedCustomerOrderInfoCard';
import SalesOrderLinesEditor from '#/modules/crmMgmt/salesOrder/components/SalesOrderLinesEditor';
import SalesOrderMainFieldsSection from '#/modules/crmMgmt/salesOrder/components/SalesOrderMainFieldsSection';
import SalesOrderShippingSection from '#/modules/crmMgmt/salesOrder/components/SalesOrderShippingSection';
import { getSalesOrderApiErrorMessage } from '#/modules/crmMgmt/salesOrder/helper/apiError';
import {
  mapCustomerDetailToSalesOrderShippingPrefill,
} from '#/modules/crmMgmt/salesOrder/helper/mapCustomerOrderDetailToSalesOrderCreateDraft';
import {
  SALES_ORDER_FIELD_LABELS,
  SALES_ORDER_FORM_DEFAULT_VALUES,
  resolveSalesOrderFieldLabel,
  salesOrderFormSchema,
} from '#/modules/crmMgmt/salesOrder/schemas/salesOrderSchema';
import PageShell from '#/modules/infra/layout/PageShell';
import {
  customerDetailQueryKey,
  lookupQueryKey,
  salesOrderDetailQueryKey,
  salesOrderListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

/**
 * 無客訂：手填表單 → POST /crm/sales-order。
 */
const StandaloneSalesOrderCreateForm = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
  } = useFormValidationSummary();

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(salesOrderFormSchema),
    defaultValues: SALES_ORDER_FORM_DEFAULT_VALUES,
  });

  const watchedCustomerId = useWatch({ control, name: 'customerId' });

  const customerOptionsQuery = useQuery({
    queryKey: lookupQueryKey('customerSelect'),
    queryFn: fetchCustomerSelectOptions,
  });

  const customerDetailQuery = useQuery({
    queryKey: customerDetailQueryKey(watchedCustomerId),
    queryFn: () => fetchCustomerDetail(watchedCustomerId),
    enabled: Boolean(watchedCustomerId),
  });

  useEffect(() => {
    resetValidationSummary();
    reset(SALES_ORDER_FORM_DEFAULT_VALUES);
  }, [reset, resetValidationSummary]);

  useEffect(() => {
    const customerDetail = customerDetailQuery.data?.data;
    if (!watchedCustomerId || !customerDetail) {
      return;
    }
    const prefill = mapCustomerDetailToSalesOrderShippingPrefill(customerDetail);
    setValue('billingAddressId', prefill.billingAddressId, { shouldDirty: true });
    setValue('shippingAddressId', prefill.shippingAddressId, { shouldDirty: true });
    setValue('shippingContactId', prefill.shippingContactId, { shouldDirty: true });
  }, [
    watchedCustomerId,
    customerDetailQuery.data,
    setValue,
  ]);

  const customerOptions = useMemo(
    () => customerOptionsQuery.data ?? [],
    [customerOptionsQuery.data],
  );

  const createMutation = useMutation({
    mutationFn: async (values) => {
      if (!values.customerId) {
        throw new Error('請選擇客戶');
      }
      return createSalesOrder(values);
    },
  });

  function handleInvalidSubmit() {
    markSubmitAttempted();
    toast.warning('請先修正表單中的錯誤');
  }

  async function onSubmit(values) {
    markSubmitAttempted();
    if (!values.customerId) {
      toast.warning('請選擇客戶，或改為關聯客戶訂單');
      return;
    }

    try {
      const result = await createMutation.mutateAsync(values);
      const salesOrderId = result?.data?.id;
      toast.success(result?.message || '銷貨單已建立');

      await queryClient.invalidateQueries({
        queryKey: salesOrderListQueryKeyPrefix(),
      });
      if (salesOrderId) {
        await queryClient.invalidateQueries({
          queryKey: salesOrderDetailQueryKey(salesOrderId),
        });
        navigate(ROUTES.salesOrderEdit(salesOrderId), { replace: true });
        return;
      }
      toast.warning('建立成功但未取得銷貨單編號');
    } catch (error) {
      const message = error?.message && !error?.response
        ? error.message
        : getSalesOrderApiErrorMessage(error, '建立銷貨單失敗');
      toast.error(message);
    }
  }

  const isPending = createMutation.isPending;

  return (
    <>
      <FormValidationSummary
        show={isSubmitAttempted}
        errors={errors}
        fieldLabels={SALES_ORDER_FIELD_LABELS}
        fieldLabelResolver={resolveSalesOrderFieldLabel}
      />

      <form
        className="sheet create_edit create_edit_form create_edit_form--page-scroll"
        onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
        noValidate
      >
        <div className="create_edit_form__body divide-y divide-gray-500">
          <section className="pb-6">
            <div className="form-block">
              <h2 className="form-block__title">客戶</h2>
            </div>
            <div
              className={cn(
                'grid grid-cols-1 gap-3',
                '@xl/sheet:grid-cols-2',
              )}
            >
              <FloatingSelect
                id="customerId"
                label="客戶"
                options={customerOptions}
                control={control}
                errors={errors}
                isDisabled={customerOptionsQuery.isLoading}
                noOptionsMessage="無可用客戶"
              />
            </div>
          </section>

          <SalesOrderMainFieldsSection
            control={control}
            register={register}
            errors={errors}
          />
          <SalesOrderShippingSection
            control={control}
            errors={errors}
            addresses={customerDetailQuery.data?.data?.addresses ?? []}
            contacts={customerDetailQuery.data?.data?.contacts ?? []}
            snapshot={null}
            isDisabled={!watchedCustomerId || customerDetailQuery.isLoading}
            noCustomerMessage={
              watchedCustomerId
                ? '此客戶無可用地址／聯絡人'
                : '請先選擇客戶或關聯客訂'
            }
          />
          <SalesOrderLinesEditor
            control={control}
            register={register}
            errors={errors}
          />
        </div>

        <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" asChild>
            <Link to={ROUTES.salesOrder}>取消</Link>
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? '建立中…' : '建立銷貨單'}
          </Button>
        </div>
      </form>
    </>
  );
};

/**
 * 銷貨單建立：
 * - 有客訂 → CreateSalesOrderScheduleForm（多預交日分批 POST …/sales-order）
 * - 無客訂 → 手填 → POST /crm/sales-order
 */
const CreateSalesOrderPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerOrderId = searchParams.get('customerOrderId')?.trim() || '';
  const [pickerOpen, setPickerOpen] = useState(false);

  function handlePickerConfirm(row) {
    if (!row?.id) {
      return;
    }
    navigate(
      `${ROUTES.salesOrderCreate}?customerOrderId=${encodeURIComponent(row.id)}`,
      { replace: true },
    );
  }

  function handleClearCustomerOrder() {
    navigate(ROUTES.salesOrderCreate, { replace: true });
  }

  const pageTitle = customerOrderId ? '建立銷貨排程' : '新增銷貨單';

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="w-full">
          <div className="create_edit_page__header">
            <div className={cn('flex items-center gap-2')}>
              <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
                <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
              </h1>
              <Button type="button" variant="outline" asChild>
                <Link to={ROUTES.salesOrder}>返回列表</Link>
              </Button>
              {customerOrderId ? (
                <Button type="button" variant="outline" asChild>
                  <Link to={ROUTES.customerOrderDetail(customerOrderId)}>
                    返回客訂
                  </Link>
                </Button>
              ) : null}
            </div>
          </div>

          <div className="mx-auto max-w-6xl space-y-4">
            <section className="rounded border border-slate-200 bg-panel p-4">
              <div className="form-block">
                <h2 className="form-block__title">來源客戶訂單（選填）</h2>
                <p className="form-block__description">
                  {customerOrderId
                    ? '依預交日分批建立銷貨單草稿；可只勾選部分明細與部分數量。'
                    : '關聯客訂改走分批建單；清除後改為手填並必選客戶。'}
                </p>
              </div>
              {customerOrderId ? (
                <div className="mt-3 space-y-3">
                  <LinkedCustomerOrderInfoCard customerOrderId={customerOrderId} />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPickerOpen(true)}
                    >
                      更換客訂
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleClearCustomerOrder}
                    >
                      清除關聯
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-3">
                  <Button type="button" onClick={() => setPickerOpen(true)}>
                    選擇客戶訂單
                  </Button>
                </div>
              )}
            </section>

            {customerOrderId ? (
              <CreateSalesOrderScheduleForm customerOrderId={customerOrderId} />
            ) : (
              <StandaloneSalesOrderCreateForm />
            )}
          </div>
        </div>
      </div>

      <CustomerOrderPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialSelectedId={customerOrderId || null}
        onConfirm={handlePickerConfirm}
      />
    </PageShell>
  );
};

export default CreateSalesOrderPage;
